import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, RefreshCw, ExternalLink, Rocket, Server, Activity, ShieldAlert, Boxes, Clock, Globe } from "lucide-react";
import api, { notifyError } from "@/lib/api";
import PlatformLayout from "@/components/PlatformLayout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { HealthBadge, HealthScoreRing } from "./MonitoringBadges";

function fmt(iso) { if (!iso) return "—"; try { return new Date(iso).toLocaleString("id-ID"); } catch { return iso; } }

function Row({ label, value, mono = false }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1 border-b border-white/5 last:border-0">
      <div className="text-[10px] text-white/50 uppercase tracking-wider">{label}</div>
      <div className={`text-xs text-white text-right min-w-0 truncate ${mono ? "font-mono" : ""}`}>{value ?? "—"}</div>
    </div>
  );
}

function Section({ title, icon: Icon, testid, children }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        {Icon ? <Icon size={14} className="text-emerald-300" /> : null}
        <div className="text-[11px] uppercase tracking-widest text-white/40 font-bold">{title}</div>
      </div>
      {children}
    </div>
  );
}

export default function MonitoringDetail() {
  const { tenantId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, h] = await Promise.all([
        api.get(`/platform/monitoring/websites/${tenantId}`),
        api.get(`/platform/monitoring/history/${tenantId}`),
      ]);
      setData(d.data);
      setHistory(h.data.snapshots || []);
    } catch (e) { notifyError(e); setData(null); setHistory([]); }
    finally { setLoading(false); }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setBusy(true);
    try {
      await api.post(`/platform/monitoring/refresh/${tenantId}`, {});
      toast.success("Snapshot tersimpan.");
      load();
    } catch (e) { notifyError(e); } finally { setBusy(false); }
  };

  if (loading) {
    return (
      <PlatformLayout title="Monitoring Detail">
        <div className="text-white/40 text-sm p-6 rounded-2xl border border-white/10 text-center" data-testid="mond-loading">Memuat…</div>
      </PlatformLayout>
    );
  }

  if (!data) {
    return (
      <PlatformLayout title="Monitoring Detail">
        <div className="p-8 rounded-2xl border border-white/10 text-center space-y-3" data-testid="mond-not-found">
          <div className="text-white/70 text-sm">Tenant tidak ditemukan atau sudah dihapus.</div>
          <Button variant="ghost" onClick={() => nav("/platform/monitoring")} className="text-white/80 hover:text-white hover:bg-white/10" data-testid="mond-not-found-back">
            <ArrowLeft size={14} className="mr-1" /> Kembali ke Monitoring
          </Button>
        </div>
      </PlatformLayout>
    );
  }

  const w = data.website || {};
  const score = data.health_score ?? 0;
  const status = data.status || "UNKNOWN";

  return (
    <PlatformLayout title={w.tenant_name || "Monitoring Detail"}>
      <div className="mb-4 flex items-center gap-2 flex-wrap">
        <Button variant="ghost" onClick={() => nav("/platform/monitoring")} className="text-white/60 hover:text-white hover:bg-white/10" data-testid="mond-back">
          <ArrowLeft size={14} className="mr-1" /> Kembali
        </Button>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <Button variant="ghost" onClick={refresh} disabled={busy} className="text-white/60 hover:text-white hover:bg-white/10" data-testid="mond-refresh">
            <RefreshCw size={14} className={`mr-1 ${busy ? "animate-spin" : ""}`} /> Refresh Tenant
          </Button>
          {w.domain && (
            <a href={`https://${w.domain}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 text-xs font-bold" data-testid="mond-open-website">
              <ExternalLink size={12} /> Open Website
            </a>
          )}
          <Link to={`/platform/websites/${tenantId}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-purple-500/40 text-purple-300 hover:bg-purple-500/10 text-xs font-bold" data-testid="mond-open-website-engine">
            <Globe size={12} /> Website Engine
          </Link>
          {w.deployment_id && (
            <Link to={`/platform/deployments/${w.deployment_id}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-sky-500/40 text-sky-300 hover:bg-sky-500/10 text-xs font-bold" data-testid="mond-open-deployment">
              <Rocket size={12} /> Deployment
            </Link>
          )}
          {w.last_infra_execution_id && (
            <Link to={`/platform/infrastructure/${w.last_infra_execution_id}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-indigo-500/40 text-indigo-300 hover:bg-indigo-500/10 text-xs font-bold" data-testid="mond-open-infra">
              <Server size={12} /> Infrastructure
            </Link>
          )}
        </div>
      </div>

      {/* Overview */}
      <div className="rounded-2xl border border-white/10 bg-white/5 p-5 mb-5 flex flex-wrap items-center gap-6" data-testid="mond-overview">
        <HealthScoreRing value={score} size={110} stroke={11} testid="mond-ring" />
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="text-white font-display font-black text-lg truncate" data-testid="mond-tenant-name">{w.tenant_name}</div>
            <HealthBadge status={status} />
            <span className="text-[10px] text-white/40 font-mono">· {w.tenant_code}</span>
          </div>
          <div className="text-xs text-white/60 font-mono truncate">{w.domain || "—"}</div>
          <div className="text-[10px] text-white/40 pt-1">Environment: {w.environment} · Plan: {w.plan}</div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Section title="Website Health" icon={Activity} testid="mond-section-website">
          <Row label="Website Status" value={w.website_status} />
          <Row label="Health Status" value={w.health_status} />
          <Row label="HTTP Status" value={w.http_status} mono />
          <Row label="Response Time" value={w.response_time != null ? `${(w.response_time * 1000).toFixed(0)} ms` : "—"} mono />
          <Row label="Last Health Check" value={fmt(w.last_health_check)} />
        </Section>

        <Section title="API + Containers" icon={Boxes} testid="mond-section-api">
          <Row label="Backend" value={w.backend_status} mono />
          <Row label="Frontend" value={w.frontend_status} mono />
          <Row label="Nginx" value={w.nginx_status} mono />
          <Row label="Database" value={w.database_status} mono />
          <Row label="Total Containers" value={w.docker_container_count} />
        </Section>

        <Section title="SSL + Domain" icon={ShieldAlert} testid="mond-section-ssl">
          <Row label="Domain" value={w.domain} mono />
          <Row label="SSL Status" value={w.ssl_status} />
          <Row label="SSL Expires" value={fmt(w.ssl_expires_at)} />
          <Row label="Storage" value={w.storage_usage != null ? `${w.storage_usage} MB` : "—"} />
        </Section>

        <Section title="Infrastructure Health" icon={Server} testid="mond-section-infra">
          <Row label="Last Execution" value={w.last_infra_execution_number} mono />
          <Row label="Adapter" value={w.last_infra_adapter} />
          <Row label="Workspace" value={w.workspace_path} mono />
          <Row label="Deployment Status" value={w.deployment_status} />
        </Section>

        <Section title="Deployment Health" icon={Rocket} testid="mond-section-deployment">
          <Row label="Latest Deployment" value={w.deployment_number} mono />
          <Row label="Version" value={w.version} mono />
          <Row label="Last Deployment" value={fmt(w.last_deployment)} />
          <Row label="Subscription" value={w.subscription_status} />
        </Section>

        <Section title="Health History" icon={Clock} testid="mond-section-history">
          {history.length === 0 ? (
            <div className="text-[10px] text-white/40 italic py-2" data-testid="mond-history-empty">Belum ada snapshot. Klik Refresh Tenant untuk membuat.</div>
          ) : (
            <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
              {history.slice(0, 20).map((h) => (
                <div key={h.id} className="rounded-lg border border-white/10 bg-black/30 p-2" data-testid={`mond-history-${h.id}`}>
                  <div className="flex items-center gap-2">
                    <HealthBadge status={h.status} size="sm" />
                    <span className="ml-auto text-[10px] font-mono text-white/60">score {h.health_score}</span>
                  </div>
                  <div className="text-[10px] text-white/40 font-mono mt-0.5">{fmt(h.checked_at)}</div>
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>
    </PlatformLayout>
  );
}
